import React, { useState } from "react";
import {
  Paper,
  Text,
  Title,
  Badge,
  Table,
  Button,
  Group,
  TextInput,
  Modal,
  Box,
  Alert,
  Tooltip,
} from "@mantine/core";
import { useForm, isEmail } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { djangoAxiosInstance } from "../../../api";

const UsersSection = ({ subscription, onUpdateUsers }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");

  const [removeModalOpened, setRemoveModalOpened] = useState(false);
  const [removeStatus, setRemoveStatus] = useState({
    success: false,
    message: "",
  });
  const [seatToRemove, setSeatToRemove] = useState(null);

  const form = useForm({
    initialValues: {
      email: "",
    },
    validate: {
      email: isEmail("Please enter a valid email"),
    },
  });

  if (!subscription) return null;

  const users = subscription.subscription_seats || [];
  const seatLimitExceeded = users.length >= subscription.seat_limit;

  const handleRemoveClick = (seatId) => {
    setSeatToRemove(seatId);
    setRemoveStatus({
      success: null,
      message: "Are you sure you want to remove this user from your subscription?",
    });
    setRemoveModalOpened(true);
  };

  const handleRemove = async (seatId) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const headers = {};
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await djangoAxiosInstance.delete(
        `/subscriptions/${subscription.id}/subscription-seats/${seatId}/`,
        { headers },
      );

      if (response.status === 204) {
        const updatedUsers = users.filter((seat) => seat.id !== seatId);
        onUpdateUsers(updatedUsers);

        setRemoveStatus({
          success: true,
          message: "User was successfully removed from subscription.",
        });
      } else {
        throw new Error(`Unexpected response status: ${response.status}`);
      }
    } catch (err) {
      console.error("Error removing user:", err);

      setRemoveStatus({
        success: false,
        message:
          err.response?.data?.message ||
          "Failed to remove user. Please try again.",
      });
    } finally {
      setSeatToRemove(null);
    }
  };

  const handleInvite = async (values) => {
    setInviteLoading(true);
    setInviteError("");

    try {
      const accessToken = localStorage.getItem("access_token");
      const headers = {};
      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      const response = await djangoAxiosInstance.post(
        `/subscriptions/${subscription.id}/subscription-seats/`,
        { email: values.email },
        { headers },
      );

      const newSeat = response.data;
      onUpdateUsers([...users, newSeat]);

      form.reset();
      close();
    } catch (err) {
      console.error("Error inviting user:", err);

      if (err.response && err.response.data) {
        const data = err.response.data;
        const emailErrors = data.email;
        const nonFieldErrors = data.non_field_errors;

        if (emailErrors) {
          setInviteError(
            Array.isArray(emailErrors) ? emailErrors.join(", ") : emailErrors,
          );
        }

        if (nonFieldErrors) {
          setInviteError(
            Array.isArray(nonFieldErrors)
              ? nonFieldErrors.join(", ")
              : nonFieldErrors,
          );
        }
      } else {
        setInviteError("Network error. Please check your connection.");
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleModalOpen = () => {
    setInviteError("");
    form.reset();
    open();
  };

  const tableData = {
    head: ["Email", "Name", "Status", "Actions"],
    body: users.map((seat) => [
      seat.user.email,
      `${seat.user.first_name} ${seat.user.last_name}`.trim() || "—",
      <Badge color={seat.user.is_active ? "green" : "red"}>
        {seat.user.is_active ? "User seat activated" : "User seat inactive"}
      </Badge>,
      <Group>
        <Tooltip label="Remove this user from your subscription list">
          <Button
            size="xs"
            variant="filled"
            color="red"
            onClick={() => handleRemoveClick(seat.id)}
          >
            Remove
          </Button>
        </Tooltip>
      </Group>,
    ]),
  };

  return (
    <Paper shadow="md" p="md" withBorder mt="md">
      <Group mb="md" justify="space-between">
        <Title order={2}>Invited Users</Title>
        <Group>
          <Text size="sm">
            {users.length} of {subscription.seat_limit} seats used
          </Text>
          <Button onClick={handleModalOpen} disabled={seatLimitExceeded}>Invite User</Button>
        </Group>
      </Group>

      <Table striped highlightOnHover data={tableData} />

      <Modal opened={opened} onClose={close} title="Invite New User" centered>
        <Box component="form" onSubmit={form.onSubmit(handleInvite)}>
          {inviteError && (
            <Alert color="red" title="Error" mb="md">
              {inviteError}
            </Alert>
          )}

          <TextInput
            label="Email"
            placeholder="Enter user email"
            required
            mb="md"
            {...form.getInputProps("email")}
          />

          <Group mt="md" justify="space-between">
            <Button variant="outline" onClick={close} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={inviteLoading}>
              Invite User
            </Button>
          </Group>
        </Box>
      </Modal>

      <Modal
        opened={removeModalOpened}
        onClose={() => setRemoveModalOpened(false)}
        title={removeStatus.success === null ? "Remove subscription seat" : removeStatus.success ? "Success" : "Error"}
        centered
      >
        <Alert
          color={removeStatus.success === null ? "blue" : removeStatus.success ? "green" : "red"}
          title={removeStatus.success === null ? "Confirm" : removeStatus.success ? "Success" : "Error"}
          mb="md"
        >
          {removeStatus.message}
        </Alert>

        <Group mt="md" justify="space-between">
          {removeStatus.success === null ? (
            <>
              <Button variant="outline" onClick={() => {
                setRemoveModalOpened(false);
                setSeatToRemove(null);
              }}>
                Cancel
              </Button>
              <Button color="red" onClick={() => handleRemove(seatToRemove)}>
                Yes, remove user
              </Button>
            </>
          ) : (
            <Button onClick={() => setRemoveModalOpened(false)}>Close</Button>
          )}
        </Group>
      </Modal>
    </Paper>
  );
};

export default UsersSection;